<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { monthGrid, formatDateGSheet } from '@/utils.ts/date';
import CalendarDay from './calendar-day.vue';
import { DEFAULT_LABELS, type CalendarLabels, type DayAvailability, type VenueFilter } from './types';

const {
  availability,
  venueFilter,
  initialYear,
  initialMonth,
  labels = DEFAULT_LABELS,
} = defineProps<{
  availability: Map<string, DayAvailability>;
  venueFilter: VenueFilter;
  initialYear: number;
  initialMonth: number; // 0-indexed
  labels?: CalendarLabels;
}>();

const model = defineModel<Date | undefined>();

const displayYear = ref(initialYear);
const displayMonth = ref(initialMonth); // 0-indexed

// Month arithmetic on a single index avoids year-wrap bugs.
const monthIndex = (y: number, m: number) => y * 12 + m;
const displayIndex = computed(() => monthIndex(displayYear.value, displayMonth.value));

// initialYear/initialMonth only pick the month the calendar opens on (e.g. a
// restored booking's month). Navigation bounds must also cover any earlier
// or later month present in `availability`, so a user can page back to
// months before the opening month when they still have availability.
const minIndex = computed(() => {
  let min = monthIndex(initialYear, initialMonth);
  for (const date of availability.keys()) {
    const idx = monthIndex(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1);
    if (idx < min) min = idx;
  }
  return min;
});
const maxIndex = computed(() => {
  let max = monthIndex(initialYear, initialMonth);
  for (const date of availability.keys()) {
    const idx = monthIndex(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1);
    if (idx > max) max = idx;
  }
  return max;
});

const monthLabel = computed(() =>
  new Intl.DateTimeFormat('fr', { month: 'long', year: 'numeric' }).format(new Date(displayYear.value, displayMonth.value, 1)),
);

const weekdays = computed(() => {
  const short = new Intl.DateTimeFormat('fr', { weekday: 'short' });
  const long = new Intl.DateTimeFormat('fr', { weekday: 'long' });
  // 2024-01-01 is a Monday; format seven consecutive days.
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 1 + i);
    return { short: short.format(d), long: long.format(d) };
  });
});

const weeks = computed(() => monthGrid(displayYear.value, displayMonth.value));

const monthEmpty = computed(() =>
  weeks.value.flat().every((d) => d === null || dayState(d) === 'unavailable'),
);

const selectedStr = computed(() => (model.value ? formatDateGSheet(model.value) : ''));

function dayState(day: string): 'available' | 'unavailable' {
  const entry = availability.get(day);
  if (!entry) return 'unavailable';
  if (venueFilter !== 'both' && !entry.venues.includes(venueFilter)) return 'unavailable';
  return 'available';
}

function shiftMonth(delta: 1 | -1) {
  const target = displayIndex.value + delta;
  if (target < minIndex.value || target > maxIndex.value) return;
  displayYear.value = Math.floor(target / 12);
  displayMonth.value = target % 12;
}

function select(day: string) {
  model.value = new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}

const focusedDay = ref('');

function addDays(day: string, delta: number): string {
  const d = new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
  d.setDate(d.getDate() + delta);
  return formatDateGSheet(d);
}

async function moveFocus(target: string) {
  const y = Number(target.slice(0, 4));
  const m = Number(target.slice(5, 7)) - 1;
  const idx = monthIndex(y, m);
  if (idx < minIndex.value || idx > maxIndex.value) return; // clamp at data bounds
  if (idx !== displayIndex.value) {
    displayYear.value = y;
    displayMonth.value = m;
    await nextTick();
  }
  focusedDay.value = target;
  rootEl.value?.querySelector<HTMLButtonElement>(`[data-date="${target}"]`)?.focus();
}

const rootEl = ref<HTMLElement | null>(null);

function onGridKeydown(e: KeyboardEvent) {
  const current = (e.target as HTMLElement).getAttribute?.('data-date');
  if (!current) return;
  const handlers: Record<string, () => string | null> = {
    ArrowLeft: () => addDays(current, -1),
    ArrowRight: () => addDays(current, 1),
    ArrowUp: () => addDays(current, -7),
    ArrowDown: () => addDays(current, 7),
    Home: () => {
      const dow = (new Date(Number(current.slice(0, 4)), Number(current.slice(5, 7)) - 1, Number(current.slice(8, 10))).getDay() + 6) % 7;
      return addDays(current, -dow);
    },
    End: () => {
      const dow = (new Date(Number(current.slice(0, 4)), Number(current.slice(5, 7)) - 1, Number(current.slice(8, 10))).getDay() + 6) % 7;
      return addDays(current, 6 - dow);
    },
  };
  if (e.key === 'PageUp' || e.key === 'PageDown') {
    e.preventDefault();
    shiftMonth(e.key === 'PageDown' ? 1 : -1);
    nextTick(() => {
      rootEl.value?.querySelector<HTMLButtonElement>(`[data-date="${tabbableDay.value}"]`)?.focus();
    });
    return;
  }
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    if (dayState(current) === 'available') select(current);
    return;
  }
  const handler = handlers[e.key];
  if (!handler) return;
  e.preventDefault();
  const target = handler();
  if (target) moveFocus(target);
}

// Roving tabindex target: the focused day when present, else the selected
// day when visible, else the first available day of the displayed month.
const tabbableDay = computed(() => {
  const flat = weeks.value.flat().filter((d): d is string => d !== null);
  if (flat.includes(focusedDay.value)) return focusedDay.value;
  if (flat.includes(selectedStr.value)) return selectedStr.value;
  return flat.find((d) => dayState(d) === 'available') ?? flat[0] ?? '';
});
</script>

<template>
  <div class="booking-calendar">
    <div class="bc-header">
      <span class="bc-month" data-testid="calendar-month-label" aria-live="polite">{{ monthLabel }}</span>
      <span class="bc-nav">
        <button
          type="button"
          class="bc-nav-btn"
          data-testid="calendar-prev"
          aria-label="Mois précédent"
          :disabled="displayIndex <= minIndex"
          @click="shiftMonth(-1)"
        >‹</button>
        <button
          type="button"
          class="bc-nav-btn"
          data-testid="calendar-next"
          aria-label="Mois suivant"
          :disabled="displayIndex >= maxIndex"
          @click="shiftMonth(1)"
        >›</button>
      </span>
    </div>
    <div role="grid" data-testid="date-picker" :aria-label="monthLabel" ref="rootEl" @keydown="onGridKeydown">
      <div role="row" class="bc-week bc-week--head">
        <span v-for="wd in weekdays" :key="wd.long" role="columnheader" class="bc-weekday" :aria-label="wd.long">{{ wd.short }}</span>
      </div>
      <div v-for="(week, wi) in weeks" :key="wi" role="row" class="bc-week">
        <span v-for="(day, di) in week" :key="di" role="gridcell" class="bc-cell">
          <CalendarDay
            :day="day"
            :state="day ? dayState(day) : 'blank'"
            :availability="day ? availability.get(day) : undefined"
            show-venue-bar
            :filter="venueFilter"
            :selected="day === selectedStr"
            :tabbable="day === tabbableDay"
            :labels="labels"
            @select="select"
          />
        </span>
      </div>
    </div>
    <p v-if="monthEmpty" class="bc-empty" data-testid="calendar-empty-month" aria-live="polite">{{ labels.emptyMonth }}</p>
  </div>
</template>

<style scoped>
.booking-calendar { border: 1px solid #cfd6e5; border-radius: 8px; padding: 8px; }
.bc-header { display: flex; align-items: center; justify-content: space-between; padding: 4px 8px 8px; }
.bc-month { font-size: 18px; font-weight: 600; text-transform: capitalize; }
.bc-nav-btn {
  min-width: 44px;
  min-height: 44px;
  border: none;
  background: transparent;
  font-size: 22px;
  cursor: pointer;
  border-radius: 8px;
}
.bc-nav-btn:hover:not(:disabled) { background: #f0ece6; }
.bc-nav-btn:disabled { color: #c0c0c0; cursor: not-allowed; }
.bc-week { display: grid; grid-template-columns: repeat(7, 1fr); }
.bc-weekday { text-align: center; font-size: 12px; color: #6b6b6b; padding: 4px 0; }
.bc-cell { display: block; }
.bc-empty { margin: 8px 4px 0; font-size: 14px; color: #6b6b6b; }
</style>
